/**
 * Service Worker Registration Utility
 *
 * MULTI-TENANT PWA Support:
 * - Each tenant subdomain gets isolated service worker + cache
 * - Main domain (livecargo.ee) has PWA disabled
 * - Automatic cleanup when PWA shouldn't be active
 */

interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onOffline?: () => void;
  onOnline?: () => void;
}

/**
 * Check if we should enable PWA features
 *
 * MULTI-TENANT LOGIC:
 * - Enable ONLY on tenant subdomains (acme.livecargo.ee, beta.livecargo.ee, etc.)
 * - Disable on main domain (livecargo.ee, www.livecargo.ee)
 * - Enable on localhost for testing
 */
export const shouldEnablePWA = (): boolean => {
  const pathname = window.location.pathname;
  const hostname = window.location.hostname;

  const isDriverPage =
    pathname === "/" ||
    pathname === "/shipments" ||
    pathname.startsWith("/shipments/") ||
    pathname.startsWith("/driver/") ||
    pathname.startsWith("/shipment-drivers-view");

  const parts = hostname.split(".");

  // Detect livecargo root domain dynamically
  const isLivecargoDomain = hostname.includes("livecargo.");

  const isMainDomain =
    hostname === "livecargo.ee" ||
    hostname === "livecargo.eu" ||
    hostname === "www.livecargo.ee" ||
    hostname === "www.livecargo.eu";

  const isSubdomain = parts.length >= 3 && isLivecargoDomain;

  const isLocalhost =
    hostname.endsWith(".flowcargo.local") || hostname === "localhost";

  return isDriverPage && (isSubdomain || isLocalhost) && !isMainDomain;
};

/**
 * Register service worker
 */
export const registerServiceWorker = async (
  config?: ServiceWorkerConfig,
): Promise<void> => {
  // Check if service workers are supported
  if (!("serviceWorker" in navigator)) {
    // console.log("[PWA] Service workers not supported in this browser");
    return;
  }

  // Check if we should enable PWA
  if (!shouldEnablePWA()) {
    // console.log("[PWA] PWA disabled for this page/domain");

    // CLEANUP: Unregister any existing service workers on wrong domains
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
        // console.log("[PWA] Unregistered service worker from wrong domain");
      }

      // Clear any leftover caches from other tenants
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        if (cacheName.startsWith("livecargo-")) {
          await caches.delete(cacheName);
          // console.log("[PWA] Cleared cache:", cacheName);
        }
      }
    } catch (error) {
      console.warn("[PWA] Failed to cleanup:", error);
    }

    return;
  }

  // TENANT LOGGING
  // const hostname = window.location.hostname;
  // const tenantId = hostname.split(".")[0];
  // console.log("[PWA] Registering service worker for tenant:", tenantId);

  try {
    const registration = await navigator.serviceWorker.register(
      "/service-worker.js",
      {
        scope: "/",
        updateViaCache: "none", // Always check for updates
      },
    );

    // console.log("[PWA] Service worker registered:", registration.scope);
    // console.log("[PWA] Tenant:", tenantId);
    // console.log("[PWA] Origin:", window.location.origin);

    // Handle updates
    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      newWorker.addEventListener("statechange", () => {
        if (
          newWorker.state === "installed" &&
          navigator.serviceWorker.controller
        ) {
          // New service worker available
          // console.log("[PWA] New version available");
          config?.onUpdate?.(registration);
        }
      });
    });

    // Success callback
    if (registration.active) {
      config?.onSuccess?.(registration);
    } else if (registration.installing || registration.waiting) {
      // Wait for service worker to become active
      const sw = registration.installing || registration.waiting;
      sw?.addEventListener("statechange", function handler(e) {
        if ((e.target as ServiceWorker).state === "activated") {
          config?.onSuccess?.(registration);
          sw.removeEventListener("statechange", handler);
        }
      });
    }

    // Listen for online/offline events
    window.addEventListener("online", () => {
      // console.log("[PWA] Back online");
      config?.onOnline?.();
    });

    window.addEventListener("offline", () => {
      // console.log("[PWA] Gone offline");
      config?.onOffline?.();
    });
  } catch (error) {
    console.error("[PWA] Service worker registration failed:", error);
  }
};

/**
 * Unregister service worker (for rollback)
 */
export const unregisterServiceWorker = async (): Promise<void> => {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.unregister();
      // console.log("[PWA] Service worker unregistered");

      // Clear all caches
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
      // console.log("[PWA] All caches cleared");
    }
  } catch (error) {
    console.error("[PWA] Failed to unregister service worker:", error);
  }
};

/**
 * Check if app is running in standalone mode (installed PWA)
 */
export const isStandalone = (): boolean => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true
  );
};

/**
 * Get current service worker version and tenant info
 */
export const getServiceWorkerVersion = async (): Promise<{
  version: string;
  tenant: string;
  cacheName: string;
  origin: string;
} | null> => {
  if (!("serviceWorker" in navigator)) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const activeWorker = registration?.active;
    if (!registration || !activeWorker) {
      return null;
    }

    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data || null);
      };

      activeWorker.postMessage({ type: "GET_VERSION" }, [messageChannel.port2]);

      // Timeout after 1 second
      setTimeout(() => resolve(null), 1000);
    });
  } catch (error) {
    console.error("[PWA] Failed to get service worker version:", error);
    return null;
  }
};

/**
 * Force service worker update
 */
export const forceServiceWorkerUpdate = async (): Promise<void> => {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.update();
      // console.log("[PWA] Service worker update triggered");
    }
  } catch (error) {
    console.error("[PWA] Failed to update service worker:", error);
  }
};

/**
 * Clear cache for current tenant
 */
export const clearTenantCache = async (): Promise<void> => {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const activeWorker = registration?.active;

    if (activeWorker) {
      return new Promise((resolve, reject) => {
        const messageChannel = new MessageChannel();
        messageChannel.port1.onmessage = (event) => {
          if (event.data?.success) {
            // console.log("[PWA] Cache cleared successfully");
            resolve();
          } else {
            reject(new Error("Failed to clear cache"));
          }
        };

        activeWorker.postMessage({ type: "CLEAR_CACHE" }, [
          messageChannel.port2,
        ]);

        // Timeout after 2 seconds
        setTimeout(() => reject(new Error("Timeout")), 2000);
      });
    }
  } catch (error) {
    console.error("[PWA] Failed to clear cache:", error);
  }
};
